import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { HomeStackParamList } from '@/types/navigation';
import type { MeetingAnnouncementView } from '@/api/types';
import SwipeCardBase from './SwipeCard';
import type { Profile } from './types';
import { CARD_WIDTH } from './constants';
import { YStack } from '@tamagui/stacks';

type SwipeCardProps = {
  profile: Profile;
  /** Announcement ID for navigation to detail screen */
  announcementId?: string;
  /** Full announcement object to pass to detail screen (avoids refetch) */
  announcement?: MeetingAnnouncementView;
};

const SwipeCard = React.memo<SwipeCardProps>(({ profile, announcementId, announcement }) => {
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();

  const handleInfoPress = useCallback(() => {
    // Use announcementId if provided, otherwise fallback to profile.id
    const id = announcementId || profile.id;
    
    navigation.navigate('MeetingAnnouncement', { 
      announcementId: id,
      announcement, // Pass full object to avoid refetch
    });
  }, [navigation, announcementId, profile.id, announcement]);

  // On web, use flex layout; on mobile use fixed width
  const containerStyle = Platform.OS === 'web' 
    ? { flex: 1, maxWidth: 500, alignSelf: 'center' as const, width: '100%' as any }
    : { width: CARD_WIDTH };

  return (
    <YStack {...containerStyle} flex={1}>
      <SwipeCardBase
        profile={profile}
        announcementId={announcementId}
        announcement={announcement}
        onInfoPress={handleInfoPress}
      />
    </YStack>
  );
});

SwipeCard.displayName = 'SwipeCard';

export default SwipeCard;
